import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BookOpen, Play, Star, ArrowRight, Clock, Users, Check } from 'lucide-react';

const HomePage = () => {
  const { t, i18n } = useTranslation();
  const [courses, setCourses] = useState([]);
  const [showMore, setShowMore] = useState({});
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    service: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Section cours masquée - pas besoin de fetchCourses
  // useEffect(() => {
  //   fetchCourses();
  // }, [i18n.language]);

  // const fetchCourses = async () => {
  //   try {
  //     const response = await api.get('/courses', {
  //       params: { lang: i18n.language }
  //     });
  //     setCourses(response.data.courses.slice(0, 3));
  //   } catch (error) {
  //     console.error('Erreur:', error);
  //   }
  // };

  const toggleText = (section) => {
    setShowMore(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setContactForm(prev => ({ ...prev, [name]: value }));
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormStatus({ type: '', message: '' });

    try {
      // Utilisation de Formspree pour l'envoi d'emails
      const response = await fetch('https://formspree.io/f/mkokedjj', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: contactForm.name,
          email: contactForm.email,
          service: contactForm.service,
          message: contactForm.message,
          _subject: `Nouveau message de ${contactForm.name} - ${contactForm.service}`
        })
      });

      if (response.ok) {
        setFormStatus({ 
          type: 'success', 
          message: 'Message envoyé avec succès ! Nous vous répondrons dans les plus brefs délais.' 
        });
        // Réinitialiser le formulaire
        setContactForm({
          name: '',
          email: '',
          service: '',
          message: ''
        });
      } else {
        throw new Error('Erreur lors de l\'envoi');
      }
    } catch (error) {
      setFormStatus({ 
        type: 'error', 
        message: 'Erreur lors de l\'envoi du message. Veuillez réessayer.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h${mins > 0 ? ` ${mins}min` : ''}`;
    }
    return `${mins}min`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary-600 to-primary-500 text-white py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="mb-8 flex justify-center">
            <img 
              src="/logo.png" 
              alt="La Source et La Voie" 
              className="h-32 md:h-40 w-auto drop-shadow-2xl"
            />
          </div>
          <h1 className="text-4xl md:text-6xl font-bold mb-6">{t('hero.title')}</h1>
          <p className="text-xl md:text-2xl mb-4 max-w-3xl mx-auto opacity-90">
            {t('hero.subtitle1')}
          </p>
          <p className="text-lg md:text-xl mb-4 max-w-3xl mx-auto opacity-90">
            {t('hero.subtitle2')}
          </p>
          <p className="text-lg md:text-xl mb-8 max-w-3xl mx-auto opacity-90">
            {t('hero.subtitle3')}
          </p>
          
          <div className="flex flex-col md:flex-row justify-center gap-4">
            <a href="#contact" className="bg-white text-primary-600 hover:bg-gray-100 font-bold py-3 px-8 rounded-lg transition duration-300 shadow-lg">
              {t('hero.ctaConsultation')}
            </a>
            {/* TODO: Réactiver quand les cours seront prêts */}
            {/* <Link to="/courses" className="border-2 border-white text-white hover:bg-white hover:text-primary-600 font-bold py-3 px-8 rounded-lg transition duration-300">
              {t('hero.ctaCourses')}
            </Link> */}
          </div>
        </div>
      </section>

      {/* À propos */}
      <section id="apropos" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{t('about.title')}</h2>
            <div className="w-24 h-1 bg-primary-600 mx-auto"></div>
          </div>
          
          <div className="flex flex-col md:flex-row items-center gap-12">
            <div className="md:w-1/2">
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-lg shadow-xl overflow-hidden h-48">
                  <img src="/images/IMG_4707.JPG" alt="Maître Youhan - La Source et La Voie - Expert Feng Shui et Bazi" className="w-full h-full object-cover" />
                </div>
                <div className="rounded-lg shadow-xl overflow-hidden h-48">
                  <img src="/images/image1.jpeg" alt="Consultation Feng Shui - La Source et La Voie" className="w-full h-full object-cover" />
                </div>
                <div className="rounded-lg shadow-xl overflow-hidden h-48">
                  <img src="/images/image0.jpeg" alt="Pratique Feng Shui et harmonisation énergétique - La Source et La Voie" className="w-full h-full object-cover" />
                </div>
                <div className="rounded-lg shadow-xl overflow-hidden h-48">
                  <img src="/images/image4.png" alt="Enseignement Feng Shui et Bazi - La Source et La Voie" className="w-full h-full object-cover" />
                </div>
              </div>
            </div>
            
            <div className="md:w-1/2">
              <h3 className="text-2xl font-bold text-gray-800 mb-4">{t('about.name')}</h3>
              <p className="text-gray-700 mb-4">
                {t('about.bio1')}
                <br /><br />
                {t('about.bio2')}
                {showMore.about && (
                  <>
                    <br /><br />
                    {t('about.bio3').split('\n').map((line, index) => (
                      <React.Fragment key={index}>
                        {line}
                        {index < t('about.bio3').split('\n').length - 1 && <br />}
                      </React.Fragment>
                    ))}
                  </>
                )}
              </p>
              <button 
                onClick={() => toggleText('about')}
                className="text-primary-600 font-semibold hover:text-primary-700 transition-colors"
              >
                {showMore.about ? t('about.readLess') : t('about.readMore')} →
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{t('services.title')}</h2>
            <div className="w-24 h-1 bg-primary-600 mx-auto"></div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Bazi */}
            <div className="bg-white p-8 rounded-lg shadow-lg hover:shadow-xl transition duration-300">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mb-6 mx-auto">
                <BookOpen className="text-primary-600" size={32} />
              </div>
              <h3 className="text-xl font-bold text-center text-gray-900 mb-4">{t('services.bazi.title')}</h3>
              <p className="text-gray-600 mb-4 text-sm">
                {t('services.bazi.description')}
                {showMore.bazi && <> {t('services.bazi.more')}</>}
              </p>
              <button 
                onClick={() => toggleText('bazi')}
                className="text-primary-600 font-semibold hover:text-primary-700 transition-colors text-sm"
              >
                {showMore.bazi ? t('about.readLess') : t('about.readMore')} →
              </button>
            </div>

            {/* Feng Shui */}
            <div className="bg-white p-8 rounded-lg shadow-lg hover:shadow-xl transition duration-300">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mb-6 mx-auto">
                <span className="text-3xl">🏠</span>
              </div>
              <h3 className="text-xl font-bold text-center text-gray-900 mb-4">{t('services.fengshui.title')}</h3>
              <p className="text-gray-600 mb-4 text-sm">
                {t('services.fengshui.description')}
              </p>
              {showMore.fengshui && (
                <div className="text-sm">
                  <p className="font-semibold text-gray-900 mb-2">{t('services.fengshui.domains')}</p>
                  <ul className="mb-4 space-y-2">
                    {t('services.fengshui.domainsList', { returnObjects: true }).map((domain, index) => (
                      <li key={index} className="flex items-start">
                        <Check className="text-primary-600 mt-0.5 mr-2 flex-shrink-0" size={16} />
                        <span className="text-gray-600">{domain}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="font-semibold text-gray-900 mb-2">{t('services.fengshui.specialties')}</p>
                  <ul className="mb-4 space-y-2">
                    {t('services.fengshui.specialtiesList', { returnObjects: true }).map((specialty, index) => (
                      <li key={index} className="flex items-start">
                        <Check className="text-primary-600 mt-0.5 mr-2 flex-shrink-0" size={16} />
                        <span className="text-gray-600">{specialty}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-gray-600 text-sm">{t('services.fengshui.expertise')}</p>
                </div>
              )}
              <button 
                onClick={() => toggleText('fengshui')}
                className="text-primary-600 font-semibold hover:text-primary-700 transition-colors text-sm mt-2"
              >
                {showMore.fengshui ? t('about.readLess') : t('about.readMore')} →
              </button>
            </div>

            {/* Divination */}
            <div className="bg-white p-8 rounded-lg shadow-lg hover:shadow-xl transition duration-300">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mb-6 mx-auto">
                <span className="text-3xl">📅</span>
              </div>
              <h3 className="text-xl font-bold text-center text-gray-900 mb-4">{t('services.divination.title')}</h3>
              <p className="text-gray-600 mb-4 text-sm">
                {t('services.divination.description')}
              </p>
              {showMore.divination && (
                <ul className="space-y-3 text-sm">
                  {t('services.divination.methods', { returnObjects: true }).map((method, index) => (
                    <li key={index} className="flex items-start">
                      <Check className="text-primary-600 mt-0.5 mr-2 flex-shrink-0" size={16} />
                      <span className="text-gray-600">
                        <strong className="text-gray-900">{method.name} —</strong> {method.description}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              <button 
                onClick={() => toggleText('divination')}
                className="text-primary-600 font-semibold hover:text-primary-700 transition-colors text-sm mt-2"
              >
                {showMore.divination ? t('about.readLess') : t('about.readMore')} →
              </button>
            </div>

            {/* Médecine Taoïste */}
            <div className="bg-white p-8 rounded-lg shadow-lg hover:shadow-xl transition duration-300">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mb-6 mx-auto">
                <span className="text-3xl">🌿</span>
              </div>
              <h3 className="text-xl font-bold text-center text-gray-900 mb-4">{t('services.medicine.title')}</h3>
              <p className="text-gray-600 mb-4 text-sm">
                {t('services.medicine.description')}
              </p>
              {showMore.medicine && (
                <div className="text-sm text-gray-600">
                  <p>{t('services.medicine.more')}</p>
                </div>
              )}
              <button 
                onClick={() => toggleText('medicine')}
                className="text-primary-600 font-semibold hover:text-primary-700 transition-colors text-sm mt-2"
              >
                {showMore.medicine ? t('about.readLess') : t('about.readMore')} →
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Formations en ligne - TODO: Réactiver quand les cours seront prêts */}
      {false && <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{t('courses.title')}</h2>
            <div className="w-24 h-1 bg-primary-600 mx-auto"></div>
            <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
              {t('courses.subtitle')}
            </p>
          </div>

          {courses.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              {courses.map((course) => (
                <div key={course.id} className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition duration-300">
                  <div className="h-48 bg-gradient-to-r from-primary-400 to-primary-600 flex items-center justify-center">
                    {course.thumbnail ? (
                      <img src={course.thumbnail} alt={course.title} className="w-full h-full object-cover" />
                    ) : (
                      <Play className="text-white" size={64} />
                    )}
                  </div>
                  <div className="p-6">
                    <span className="inline-block px-3 py-1 bg-primary-100 text-primary-800 rounded-full text-xs font-medium mb-3">
                      {course.level}
                    </span>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{course.title}</h3>
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">{course.description}</p>
                    <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                      <div className="flex items-center">
                        <Clock className="w-4 h-4 mr-1" />
                        {formatDuration(course.duration)}
                      </div>
                      <div className="flex items-center">
                        <Users className="w-4 h-4 mr-1" />
                        {course._count?.purchases || 0} étudiants
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-2xl font-bold text-primary-600">
                        {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: course.currency }).format(course.price)}
                      </span>
                      <Link 
                        to={`/courses/${course.id}`}
                        className="btn btn-primary"
                      >
                        {t('courses.viewCourse')}
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-600">{t('courses.comingSoon')}</p>
            </div>
          )}

          <div className="text-center">
            <Link 
              to="/courses"
              className="inline-flex items-center btn btn-outline"
            >
              {t('courses.viewAll')}
              <ArrowRight className="ml-2" size={20} />
            </Link>
          </div>
        </div>
      </section>}

      {/* Témoignages */}
      <section className="py-16 bg-gradient-to-r from-primary-600 to-primary-500 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">{t('testimonials.title')}</h2>
            <div className="w-24 h-1 bg-white mx-auto opacity-50"></div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {t('testimonials.items', { returnObjects: true }).map((testimonial, index) => (
              <div key={index} className="bg-white text-gray-800 p-8 rounded-lg shadow-xl hover:transform hover:-translate-y-2 transition duration-300">
                <div className="flex items-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="text-amber-600 fill-current" size={20} />
                  ))}
                </div>
                <p className="italic mb-6">"{testimonial.text}"</p>
                <div className="flex items-center">
                  <img 
                    src={`https://randomuser.me/api/portraits/${index === 1 ? 'men' : 'women'}/${index === 0 ? '65' : index === 1 ? '32' : '44'}.jpg`} 
                    alt={testimonial.name} 
                    className="w-12 h-12 rounded-full mr-4" 
                  />
                  <div>
                    <p className="font-bold">{testimonial.name}</p>
                    <p className="text-sm text-gray-600">{testimonial.location}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vidéos - Visible uniquement en chinois */}
      {i18n.language === 'zh' && (
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{t('videos.title')}</h2>
              <div className="w-24 h-1 bg-primary-600 mx-auto"></div>
              <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
                {t('videos.subtitle')}
              </p>
            </div>

            <div className="max-w-4xl mx-auto">
              <div className="relative rounded-lg overflow-hidden shadow-2xl">
                <div className="aspect-video">
                  <iframe
                    className="w-full h-full"
                    src="https://www.youtube.com/embed/BrNR8f1rKRo"
                    title="视频分享"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Contact */}
      <section id="contact" className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{t('contact.title')}</h2>
            <div className="w-24 h-1 bg-primary-600 mx-auto"></div>
            <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
              {t('contact.subtitle')}
            </p>
          </div>

          <div className="flex justify-center mb-8">
            <img 
              src="/images/meditation.jpeg" 
              alt="Méditation et harmonisation énergétique - La Source et La Voie" 
              className="rounded-lg shadow-xl max-w-md w-full h-auto object-cover"
            />
          </div>

          <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-6">{t('contact.coordinates')}</h3>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center mr-4">
                      <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-semibold">{t('contact.email')}</p>
                      <p className="text-gray-600">lasourceetlavoie@gmail.com</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mr-4">
                      <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8.691 2.188C3.891 2.188 0 5.476 0 9.53c0 2.212 1.17 4.203 3.002 5.55a.59.59 0 0 1 .213.665l-.39 1.48c-.019.07-.048.141-.048.213 0 .163.13.295.29.295a.326.326 0 0 0 .167-.054l1.903-1.114a.864.864 0 0 1 .717-.098 10.16 10.16 0 0 0 2.837.403c.276 0 .543-.027.811-.05-.857-2.578.157-4.972 1.932-6.446 1.703-1.415 3.882-1.98 5.853-1.838-.576-3.583-4.196-6.348-8.596-6.348zM5.785 5.991c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 0 1-1.162 1.178A1.17 1.17 0 0 1 4.623 7.17c0-.651.52-1.18 1.162-1.18zm5.813 0c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 0 1-1.162 1.178 1.17 1.17 0 0 1-1.162-1.178c0-.651.52-1.18 1.162-1.18zm5.34 2.867c-1.797-.052-3.746.512-5.28 1.786-1.72 1.428-2.687 3.72-1.78 6.22.942 2.453 3.666 4.229 6.884 4.229.826 0 1.622-.12 2.361-.336a.722.722 0 0 1 .598.082l1.584.926a.272.272 0 0 0 .14.047c.134 0 .24-.111.24-.247 0-.06-.023-.12-.038-.177l-.327-1.233a.582.582 0 0 1-.023-.156.49.49 0 0 1 .201-.398C23.024 18.48 24 16.82 24 14.98c0-3.21-2.931-5.837-6.656-6.088V8.89c-.135-.01-.27-.027-.407-.03zm-2.53 3.274c.535 0 .969.44.969.982a.976.976 0 0 1-.969.983.976.976 0 0 1-.969-.983c0-.542.434-.982.969-.982zm4.844 0c.535 0 .969.44.969.982a.976.976 0 0 1-.969.983.976.976 0 0 1-.969-.983c0-.542.434-.982.969-.982z"/>
                      </svg>
                    </div>
                    <div>
                      <p className="font-semibold">{t('contact.wechat')}</p>
                      <p className="text-gray-600">Xiaoxi-Youhan</p>
                    </div>
                  </div>

                  <div className="mt-4 p-4 bg-gray-50 rounded-lg text-center">
                    <p className="font-semibold text-gray-900 mb-3">{t('contact.wechatQR')}</p>
                    <img 
                      src="/images/wechat-qr.png" 
                      alt="WeChat QR Code" 
                      className="w-48 h-48 mx-auto rounded-lg shadow-md"
                    />
                    <p className="text-sm text-gray-600 mt-2">{t('contact.scanQR')}</p>
                  </div>

                  <a href="https://www.instagram.com/la_source_et_la_voie?igsh=eWt4ZGZvODBoZWE3" target="_blank" rel="noopener noreferrer" className="flex items-start mt-6 hover:opacity-80 transition-opacity">
                    <div className="w-10 h-10 bg-gradient-to-br from-purple-100 via-pink-100 to-orange-100 rounded-full flex items-center justify-center mr-4">
                      <svg className="w-5 h-5 text-pink-600" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                      </svg>
                    </div>
                    <div>
                      <p className="font-semibold">Instagram</p>
                    </div>
                  </a>

                  <div className="mt-4 p-4 bg-gray-50 rounded-lg text-center">
                    <p className="font-semibold text-gray-900 mb-3">{t('contact.instagramQR')}</p>
                    <img 
                      src="/images/instagram-qr.png" 
                      alt="Instagram QR Code" 
                      className="w-48 h-48 mx-auto rounded-lg shadow-md"
                    />
                    <p className="text-sm text-gray-600 mt-2">{t('contact.scanQR')}</p>
                  </div>

                </div>

                <div className="mt-8">
                  <h4 className="font-semibold mb-4 text-gray-900">{t('contact.followUs')}</h4>
                  <div className="flex space-x-4">
                    <a href="https://www.tiktok.com/@youhan_fengshui" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-black rounded-full flex items-center justify-center text-white hover:scale-110 transition duration-300">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                      </svg>
                    </a>
                    <a href="https://www.youtube.com/@YouHanFengShui123" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center text-white hover:scale-110 transition duration-300">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                      </svg>
                    </a>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-6">{t('contact.sendMessage')}</h3>
                
                {formStatus.message && (
                  <div className={`mb-4 p-4 rounded-lg ${
                    formStatus.type === 'success' 
                      ? 'bg-green-100 text-green-800 border border-green-300' 
                      : 'bg-red-100 text-red-800 border border-red-300'
                  }`}>
                    {formStatus.message}
                  </div>
                )}

                <form className="space-y-4" onSubmit={handleContactSubmit}>
                  <input 
                    type="text" 
                    name="name"
                    value={contactForm.name}
                    onChange={handleContactChange}
                    placeholder={t('contact.form.name')}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    required
                  />
                  <input 
                    type="email" 
                    name="email"
                    value={contactForm.email}
                    onChange={handleContactChange}
                    placeholder={t('contact.form.email')}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    required
                  />
                  <select 
                    name="service"
                    value={contactForm.service}
                    onChange={handleContactChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    required
                  >
                    <option value="">{t('contact.form.service')}</option>
                    <option value="bazi">{t('contact.form.services.bazi')}</option>
                    <option value="fengshui">{t('contact.form.services.fengshui')}</option>
                    <option value="divination">{t('contact.form.services.divination')}</option>
                    <option value="medicine">{t('contact.form.services.medicine')}</option>
                    {/* TODO: Réactiver quand les cours seront disponibles */}
                    {/* <option value="courses">{t('contact.form.services.courses')}</option> */}
                  </select>
                  <textarea 
                    rows="4" 
                    name="message"
                    value={contactForm.message}
                    onChange={handleContactChange}
                    placeholder={t('contact.form.message')}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    required
                  ></textarea>
                  <button 
                    type="submit" 
                    className="w-full btn btn-primary"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Envoi en cours...' : t('contact.form.send')}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bloc SEO - Texte optimisé pour Google */}
      <section className="py-8 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('seo.title')}</h2>
            <p className="text-gray-700 leading-relaxed text-sm">
              {t('seo.description')}
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
